import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { Sky } from "@react-three/drei";

function sunFromAngles(elevation, azimuth) {
  const phi = THREE.MathUtils.degToRad(90 - elevation);
  const theta = THREE.MathUtils.degToRad(azimuth);
  return new THREE.Vector3().setFromSphericalCoords(1, phi, theta);
}

export function AtmosphereSky({
  turbidity = 6,
  rayleigh = 2.5,
  mieCoefficient = 0.004,
  mieDirectionalG = 0.85,
  elevation = 25,
  azimuth = 180,
  transitionSpeed = 6,
}) {
  const skyRef = useRef(null);
  const targets = useRef({
    turbidity,
    rayleigh,
    mieCoefficient,
    mieDirectionalG,
    sunPosition: sunFromAngles(elevation, azimuth),
  });

  useEffect(() => {
    targets.current.turbidity = turbidity;
    targets.current.rayleigh = rayleigh;
    targets.current.mieCoefficient = mieCoefficient;
    targets.current.mieDirectionalG = mieDirectionalG;
    targets.current.sunPosition.copy(sunFromAngles(elevation, azimuth));
  }, [turbidity, rayleigh, mieCoefficient, mieDirectionalG, elevation, azimuth]);

  useFrame((state, delta) => {
    const uniforms = skyRef.current?.material?.uniforms;
    if (!uniforms) return;
    const alpha = 1 - Math.exp(-transitionSpeed * delta);
    let moved = 0;
    const blendScalar = (key, target) => {
      const prev = uniforms[key].value;
      uniforms[key].value += (target - uniforms[key].value) * alpha;
      moved += Math.abs(uniforms[key].value - prev);
    };
    blendScalar("turbidity", targets.current.turbidity);
    blendScalar("rayleigh", targets.current.rayleigh);
    blendScalar("mieCoefficient", targets.current.mieCoefficient);
    blendScalar("mieDirectionalG", targets.current.mieDirectionalG);
    const prevSun = uniforms.sunPosition.value.clone();
    uniforms.sunPosition.value.lerp(targets.current.sunPosition, alpha);
    moved +=
      Math.abs(uniforms.sunPosition.value.x - prevSun.x) +
      Math.abs(uniforms.sunPosition.value.y - prevSun.y) +
      Math.abs(uniforms.sunPosition.value.z - prevSun.z);
    if (moved > 0.0002) state.invalidate();
  });

  return (
    <Sky
      ref={skyRef}
      distance={450000}
      turbidity={turbidity}
      rayleigh={rayleigh}
      mieCoefficient={mieCoefficient}
      mieDirectionalG={mieDirectionalG}
      sunPosition={targets.current.sunPosition}
    />
  );
}
